import React from 'react';

import Sidebar from 'components/Sidebar';
import Avatar from 'components/Avatar';
import { useUser } from 'utils/userContext';
import { backendFunction } from 'utils/backendApi';

const SELECTED_COUPON_STORAGE_KEY = 'kodeo.admin.charity.selectedCouponId';

function formatDateLabel(value) {
    if (!value) return 'No expiry';

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return value;

    return parsed.toLocaleString();
}

function formatCouponLabel(coupon) {
    const parts = [
        coupon.name || 'Unnamed coupon',
        coupon.percent_off ? `${coupon.percent_off}% off` : 'discount',
        coupon.duration || 'unknown duration',
    ];

    return parts.join(' • ');
}

function buildBatchGroups(codes) {
    const grouped = new Map();

    for (const code of codes) {
        const key = code.batch || code.promotion_code_id;
        const existing = grouped.get(key);

        if (!existing) {
            grouped.set(key, {
                key,
                batch: code.batch || 'No batch',
                campaign: code.campaign || 'charity_2026',
                partner: code.partner || '',
                notes: code.notes || '',
                expiresAt: code.expires_at || null,
                createdAt: code.created_at || code.stripe_created_at || null,
                couponId: code.coupon_id || '',
                promotionCodeIds: [code.promotion_code_id],
                codes: [code.code],
            });
            continue;
        }

        existing.promotionCodeIds.push(code.promotion_code_id);
        existing.codes.push(code.code);

        if (!existing.notes && code.notes) existing.notes = code.notes;
        if (!existing.partner && code.partner) existing.partner = code.partner;
        if (!existing.expiresAt && code.expires_at) existing.expiresAt = code.expires_at;
        if (!existing.createdAt && (code.created_at || code.stripe_created_at)) {
            existing.createdAt = code.created_at || code.stripe_created_at;
        }
    }

    return Array.from(grouped.values()).sort((left, right) => {
        const leftTime = new Date(left.createdAt || 0).getTime();
        const rightTime = new Date(right.createdAt || 0).getTime();
        return rightTime - leftTime;
    });
}

function buildCopyText(entry) {
    const lines = [
        `Batch: ${entry.batch}`,
        `Campaign: ${entry.campaign}`,
        `Partner: ${entry.partner || 'Not set'}`,
        `Expires: ${entry.expiresAt ? formatDateLabel(entry.expiresAt) : 'No expiry'}`,
        `Coupon ID: ${entry.couponId || 'Unknown'}`,
        '',
        'Codes:',
        ...entry.codes,
    ];

    if (entry.notes) {
        lines.splice(4, 0, `Notes: ${entry.notes}`);
    }

    return lines.join('\n');
}

function Field({ label, optional = false, children }) {
    return (
        <label className="flex flex-col gap-2">
            <span className="text-xs font-bold uppercase tracking-[0.18em] text-text-light">
                {label}{optional ? ' (Optional)' : ''}
            </span>
            {children}
        </label>
    );
}

function CouponPickerModal({
    coupons,
    selectedCouponId,
    configuredCouponId,
    onClose,
    onSelect,
    onClear,
}) {
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
            <div className="w-full max-w-3xl rounded-[1.75rem] border-2 border-border bg-background-pop p-6 shadow-2xl">
                <div className="flex items-start justify-between gap-4">
                    <div>
                        <p className="text-xs uppercase tracking-[0.24em] text-text-light">Stripe Coupons</p>
                        <h2 className="mt-3 text-2xl font-extrabold text-white">Choose the coupon to reuse</h2>
                        <p className="mt-2 text-sm text-text-light">
                            This choice is saved in your browser so future generations keep using the same coupon.
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="rounded-2xl border-2 border-border px-4 py-2 font-bold text-text hover:bg-background-light transition-colors"
                    >
                        Close
                    </button>
                </div>

                <div className="mt-4 flex flex-wrap gap-3">
                    <button
                        onClick={onClear}
                        className="rounded-2xl border-2 border-border px-4 py-2 text-sm font-bold text-text hover:bg-background-light transition-colors"
                    >
                        Clear browser selection
                    </button>
                    {configuredCouponId && (
                        <div className="rounded-2xl border border-primary/40 bg-primary/10 px-4 py-2 text-sm text-text">
                            Netlify env coupon: <span className="font-mono">{configuredCouponId}</span>
                        </div>
                    )}
                </div>

                <div className="mt-6 grid max-h-[28rem] gap-3 overflow-y-auto">
                    {coupons.length === 0 ? (
                        <div className="rounded-2xl border border-border bg-background-light px-4 py-10 text-center text-text-light">
                            No Stripe coupons were found.
                        </div>
                    ) : (
                        coupons.map((coupon) => {
                            const isSelected = coupon.id === selectedCouponId;

                            return (
                                <button
                                    key={coupon.id}
                                    onClick={() => onSelect(coupon.id)}
                                    className={`rounded-2xl border-2 p-4 text-left transition-colors ${
                                        isSelected
                                            ? 'border-primary bg-primary/10'
                                            : 'border-border bg-background-light hover:border-border-light'
                                    }`}
                                >
                                    <div className="flex items-start justify-between gap-4">
                                        <div>
                                            <p className="font-bold text-white">{formatCouponLabel(coupon)}</p>
                                            <p className="mt-2 text-xs text-text-light">
                                                ID: <span className="font-mono">{coupon.id}</span>
                                            </p>
                                            <p className="mt-1 text-xs text-text-light">
                                                Created: {formatDateLabel(coupon.created)}
                                            </p>
                                        </div>
                                        {isSelected && (
                                            <div className="rounded-full border border-primary px-3 py-1 text-xs font-bold uppercase tracking-[0.12em] text-primary">
                                                Selected
                                            </div>
                                        )}
                                    </div>
                                </button>
                            );
                        })
                    )}
                </div>
            </div>
        </div>
    );
}

function BatchCard({ entry, onCopy, onDelete, deleting }) {
    return (
        <div className="rounded-2xl border-2 border-border bg-background-light p-5 shadow-md">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div>
                    <p className="text-xs uppercase tracking-[0.18em] text-text-light">Saved Entry</p>
                    <h3 className="mt-2 text-xl font-bold text-white">{entry.batch}</h3>
                    <p className="mt-2 text-sm text-text-light">
                        {entry.codes.length} code{entry.codes.length === 1 ? '' : 's'} • {entry.campaign}
                    </p>
                </div>

                <div className="flex flex-wrap gap-3">
                    <button
                        onClick={() => onCopy(entry)}
                        className="rounded-2xl border-2 border-primary bg-primary px-4 py-2 font-bold text-background"
                    >
                        Copy
                    </button>
                    <button
                        onClick={() => onDelete(entry)}
                        disabled={deleting}
                        className="rounded-2xl border-2 border-border px-4 py-2 font-bold text-text hover:bg-background transition-colors disabled:opacity-60"
                    >
                        {deleting ? 'Deleting...' : 'Delete'}
                    </button>
                </div>
            </div>

            <div className="mt-5 grid gap-3 sm:grid-cols-2">
                <div>
                    <p className="text-xs uppercase tracking-[0.18em] text-text-light">Partner</p>
                    <p className="mt-1 text-sm text-text">{entry.partner || 'Not set'}</p>
                </div>
                <div>
                    <p className="text-xs uppercase tracking-[0.18em] text-text-light">Expires</p>
                    <p className="mt-1 text-sm text-text">{formatDateLabel(entry.expiresAt)}</p>
                </div>
                <div>
                    <p className="text-xs uppercase tracking-[0.18em] text-text-light">Created</p>
                    <p className="mt-1 text-sm text-text">{formatDateLabel(entry.createdAt)}</p>
                </div>
                <div>
                    <p className="text-xs uppercase tracking-[0.18em] text-text-light">Coupon</p>
                    <p className="mt-1 break-all font-mono text-sm text-text">{entry.couponId || 'Unknown'}</p>
                </div>
            </div>

            {entry.notes && (
                <div className="mt-4 rounded-xl border border-border bg-background/60 p-3">
                    <p className="text-xs uppercase tracking-[0.18em] text-text-light">Notes</p>
                    <p className="mt-2 text-sm text-text">{entry.notes}</p>
                </div>
            )}

            <div className="mt-4 rounded-xl border border-border bg-background/60 p-3">
                <p className="text-xs uppercase tracking-[0.18em] text-text-light">Codes</p>
                <p className="mt-2 break-words font-mono text-sm text-white">
                    {entry.codes.join(', ')}
                </p>
            </div>
        </div>
    );
}

function AdminCharityCodes() {
    const { user, loading } = useUser();
    const [codes, setCodes] = React.useState([]);
    const [coupons, setCoupons] = React.useState([]);
    const [pageLoading, setPageLoading] = React.useState(true);
    const [submittingSingle, setSubmittingSingle] = React.useState(false);
    const [submittingBulk, setSubmittingBulk] = React.useState(false);
    const [deletingBatchKey, setDeletingBatchKey] = React.useState('');
    const [showCouponPicker, setShowCouponPicker] = React.useState(false);
    const [message, setMessage] = React.useState('');
    const [couponMessage, setCouponMessage] = React.useState('');
    const [configuredCouponId, setConfiguredCouponId] = React.useState('');
    const [selectedCouponId, setSelectedCouponId] = React.useState(() => {
        if (typeof window === 'undefined') return '';
        return window.localStorage.getItem(SELECTED_COUPON_STORAGE_KEY) || '';
    });
    const [singleForm, setSingleForm] = React.useState({
        campaign: 'charity_2026',
        batch: '',
        partner: '',
        expiresAt: '',
        notes: '',
    });
    const [bulkForm, setBulkForm] = React.useState({
        count: 25,
        campaign: 'charity_2026',
        batch: '',
        partner: '',
        expiresAt: '',
        notes: '',
    });

    const loadData = React.useCallback(async () => {
        setPageLoading(true);
        const { data, error } = await backendFunction('admin-charity-codes', {
            method: 'GET',
            withAuth: true,
        });

        if (error) {
            setMessage(error);
        } else {
            setCodes(data?.codes || []);
            setCoupons(data?.coupons || []);
            setConfiguredCouponId(data?.configuredCouponId || '');
        }

        setPageLoading(false);
    }, []);

    React.useEffect(() => {
        if (typeof window === 'undefined') return;

        if (selectedCouponId) {
            window.localStorage.setItem(SELECTED_COUPON_STORAGE_KEY, selectedCouponId);
            return;
        }

        window.localStorage.removeItem(SELECTED_COUPON_STORAGE_KEY);
    }, [selectedCouponId]);

    React.useEffect(() => {
        if (!coupons.length || !selectedCouponId) return;

        const selectedStillExists = coupons.some((coupon) => coupon.id === selectedCouponId);
        if (!selectedStillExists) {
            setSelectedCouponId('');
        }
    }, [coupons, selectedCouponId]);

    const selectedCoupon = React.useMemo(
        () => coupons.find((coupon) => coupon.id === selectedCouponId) || null,
        [coupons, selectedCouponId]
    );

    const entries = React.useMemo(() => buildBatchGroups(codes), [codes]);

    React.useEffect(() => {
        if (!loading && user?.is_admin) {
            loadData();
        }
    }, [loading, user?.is_admin, loadData]);

    const handleGenerate = async (mode) => {
        const isBulk = mode === 'bulk';
        const form = isBulk ? bulkForm : singleForm;
        const setSubmitting = isBulk ? setSubmittingBulk : setSubmittingSingle;

        setSubmitting(true);
        setMessage('');
        setCouponMessage('');

        const { data, error } = await backendFunction('admin-charity-codes', {
            method: 'POST',
            withAuth: true,
            body: {
                mode,
                couponId: selectedCouponId || undefined,
                ...form,
            },
        });

        if (error) {
            setMessage(error);
            setSubmitting(false);
            return;
        }

        const newCodes = data?.codes || [];
        setCodes((current) => [...newCodes, ...current]);
        setMessage(
            isBulk
                ? `Generated ${newCodes.length} new charity code${newCodes.length === 1 ? '' : 's'}.`
                : 'Generated 1 new charity code.'
        );

        if (data?.coupon?.created) {
            setCouponMessage(`Stripe created a new base coupon: ${data.coupon.id}.`);
        } else if (data?.coupon?.id) {
            setCouponMessage(`Using charity coupon ${data.coupon.id}.`);
        }

        if (isBulk) {
            setBulkForm((current) => ({
                ...current,
                batch: '',
                notes: '',
            }));
        } else {
            setSingleForm((current) => ({
                ...current,
                batch: '',
                notes: '',
            }));
        }

        setSubmitting(false);
    };

    const handleCopy = async (entry) => {
        try {
            await navigator.clipboard.writeText(buildCopyText(entry));
            setMessage(`Copied ${entry.codes.length} code${entry.codes.length === 1 ? '' : 's'} from ${entry.batch}.`);
        } catch (error) {
            setMessage('Unable to copy codes right now.');
        }
    };

    const handleDelete = async (entry) => {
        const confirmed = window.confirm(
            `Delete ${entry.codes.length} code${entry.codes.length === 1 ? '' : 's'} from ${entry.batch}? This will archive the Stripe promotion code${entry.codes.length === 1 ? '' : 's'} too.`
        );
        if (!confirmed) return;

        setDeletingBatchKey(entry.key);
        setMessage('');

        const { data, error } = await backendFunction('admin-charity-codes', {
            method: 'DELETE',
            withAuth: true,
            body: {
                promotionCodeIds: entry.promotionCodeIds,
            },
        });

        if (error) {
            setMessage(error);
            setDeletingBatchKey('');
            return;
        }

        const deletedIds = new Set(data?.deletedPromotionCodeIds || []);
        setCodes((current) => current.filter((code) => !deletedIds.has(code.promotion_code_id)));
        setMessage(`Deleted ${entry.codes.length} code${entry.codes.length === 1 ? '' : 's'} from ${entry.batch}.`);
        setDeletingBatchKey('');
    };

    if (loading || !user?.is_admin) {
        return (
            <div className="flex flex-row items-center h-screen gap-0 bg-background p-4 lg:p-8 lg:pr-0 pr-0">
                <Sidebar />
                <div className="flex-grow h-full bg-background text-text flex items-center justify-center">
                    <h1 className="text-2xl">Access Denied</h1>
                </div>
            </div>
        );
    }

    return (
        <>
            {showCouponPicker && (
                <CouponPickerModal
                    coupons={coupons}
                    selectedCouponId={selectedCouponId}
                    configuredCouponId={configuredCouponId}
                    onClose={() => setShowCouponPicker(false)}
                    onSelect={(couponId) => {
                        setSelectedCouponId(couponId);
                        setShowCouponPicker(false);
                        setCouponMessage(`Selected coupon ${couponId} for future code generation in this browser.`);
                    }}
                    onClear={() => {
                        setSelectedCouponId('');
                        setShowCouponPicker(false);
                        setCouponMessage('Cleared the browser coupon selection. The backend will fall back to the Netlify env coupon or create one if needed.');
                    }}
                />
            )}

            <div className="flex flex-row items-center h-screen gap-0 bg-background p-4 lg:p-8 lg:pr-0 pr-0">
                <Sidebar />
                <div className="flex-grow h-full bg-background text-text overflow-hidden flex flex-col">
                    <div className="flex items-center justify-between px-6 py-4 border-b-2 border-border">
                        <div>
                            <h1 className="font-bold text-[1.8rem] lg:text-[2rem]">Charity Codes</h1>
                            <p className="text-text-light mt-1">
                                Generate one-time Stripe promotion codes and keep a saved history inside the admin panel.
                            </p>
                        </div>
                        <Avatar size="xs" showRings={false} dropdown={true} />
                    </div>

                    <div className="flex-grow overflow-y-auto px-6 py-6">
                        <section className="mb-6 rounded-[1.75rem] border-2 border-border bg-background-pop p-6 shadow-xl shadow-black/10">
                            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                                <div>
                                    <p className="text-xs uppercase tracking-[0.24em] text-text-light">Base Coupon</p>
                                    <h2 className="mt-3 text-2xl font-extrabold text-white">Choose the Stripe coupon to reuse</h2>
                                    <p className="mt-2 text-sm text-text-light">
                                        Your selection is remembered in this browser and used for future charity-code generations.
                                    </p>
                                </div>
                                <button
                                    onClick={() => setShowCouponPicker(true)}
                                    className="rounded-2xl border-2 border-primary bg-primary px-5 py-3 text-background font-bold"
                                >
                                    Choose coupon
                                </button>
                            </div>

                            <div className="mt-5 grid gap-4 md:grid-cols-2">
                                <div className="rounded-2xl border border-border bg-background-light p-4">
                                    <p className="text-xs uppercase tracking-[0.18em] text-text-light">Selected in this browser</p>
                                    <p className="mt-2 text-sm font-bold text-white">
                                        {selectedCoupon ? formatCouponLabel(selectedCoupon) : 'No coupon selected'}
                                    </p>
                                    {selectedCoupon && (
                                        <p className="mt-2 text-xs text-text-light">
                                            ID: <span className="font-mono">{selectedCoupon.id}</span>
                                        </p>
                                    )}
                                </div>
                                <div className="rounded-2xl border border-border bg-background-light p-4">
                                    <p className="text-xs uppercase tracking-[0.18em] text-text-light">Configured in Netlify env</p>
                                    <p className="mt-2 text-sm font-bold text-white">
                                        {configuredCouponId || 'No env coupon configured'}
                                    </p>
                                    <p className="mt-2 text-xs text-text-light">
                                        The selected browser coupon is used first. If none is selected, the backend falls back to the env coupon.
                                    </p>
                                </div>
                            </div>
                        </section>

                        <div className="grid gap-6 xl:grid-cols-[0.95fr_1.05fr]">
                            <div className="flex flex-col gap-6">
                                <section className="rounded-[1.75rem] border-2 border-primary bg-[color:color-mix(in_srgb,var(--primary)_12%,var(--background))] p-6 shadow-xl shadow-primary/10">
                                    <p className="text-xs uppercase tracking-[0.24em] text-text-light">Single Code</p>
                                    <h2 className="mt-3 text-2xl font-extrabold text-white">Generate one printable code</h2>

                                    <div className="mt-5 grid gap-4">
                                        <Field label="Batch Name" optional>
                                            <input
                                                type="text"
                                                value={singleForm.batch}
                                                onChange={(event) => setSingleForm((current) => ({ ...current, batch: event.target.value }))}
                                                placeholder="Example: spring-card-1"
                                                className="bg-background-light border-2 border-border rounded-2xl px-4 py-3 outline-none text-text"
                                            />
                                        </Field>
                                        <Field label="Partner" optional>
                                            <input
                                                type="text"
                                                value={singleForm.partner}
                                                onChange={(event) => setSingleForm((current) => ({ ...current, partner: event.target.value }))}
                                                placeholder="Who this batch belongs to"
                                                className="bg-background-light border-2 border-border rounded-2xl px-4 py-3 outline-none text-text"
                                            />
                                        </Field>
                                        <Field label="Campaign">
                                            <input
                                                type="text"
                                                value={singleForm.campaign}
                                                onChange={(event) => setSingleForm((current) => ({ ...current, campaign: event.target.value }))}
                                                placeholder="Campaign label"
                                                className="bg-background-light border-2 border-border rounded-2xl px-4 py-3 outline-none text-text"
                                            />
                                        </Field>
                                        <Field label="Expiration Date" optional>
                                            <input
                                                type="date"
                                                value={singleForm.expiresAt}
                                                onChange={(event) => setSingleForm((current) => ({ ...current, expiresAt: event.target.value }))}
                                                className="bg-background-light border-2 border-border rounded-2xl px-4 py-3 outline-none text-text"
                                            />
                                        </Field>
                                        <Field label="Notes" optional>
                                            <textarea
                                                value={singleForm.notes}
                                                onChange={(event) => setSingleForm((current) => ({ ...current, notes: event.target.value }))}
                                                placeholder="Anything useful to remember about this code"
                                                rows={3}
                                                className="bg-background-light border-2 border-border rounded-2xl px-4 py-3 outline-none text-text resize-none"
                                            />
                                        </Field>
                                    </div>

                                    <button
                                        onClick={() => handleGenerate('single')}
                                        disabled={submittingSingle}
                                        className="mt-5 w-full rounded-2xl border-2 border-primary bg-primary px-5 py-3 text-background font-bold disabled:opacity-60"
                                    >
                                        {submittingSingle ? 'Generating code...' : 'Generate one code'}
                                    </button>
                                </section>

                                <section className="rounded-[1.75rem] border-2 border-border bg-background-pop p-6 shadow-xl shadow-black/10">
                                    <p className="text-xs uppercase tracking-[0.24em] text-text-light">Bulk Batch</p>
                                    <h2 className="mt-3 text-2xl font-extrabold text-white">Generate a batch for cards</h2>

                                    <div className="mt-5 grid gap-4">
                                        <Field label="How Many Codes">
                                            <input
                                                type="number"
                                                min="1"
                                                value={bulkForm.count}
                                                onChange={(event) => setBulkForm((current) => ({ ...current, count: event.target.value }))}
                                                placeholder="Count"
                                                className="bg-background-light border-2 border-border rounded-2xl px-4 py-3 outline-none text-text"
                                            />
                                        </Field>
                                        <Field label="Batch Name" optional>
                                            <input
                                                type="text"
                                                value={bulkForm.batch}
                                                onChange={(event) => setBulkForm((current) => ({ ...current, batch: event.target.value }))}
                                                placeholder="Example: spring-2026-cards"
                                                className="bg-background-light border-2 border-border rounded-2xl px-4 py-3 outline-none text-text"
                                            />
                                        </Field>
                                        <Field label="Partner" optional>
                                            <input
                                                type="text"
                                                value={bulkForm.partner}
                                                onChange={(event) => setBulkForm((current) => ({ ...current, partner: event.target.value }))}
                                                placeholder="Who this batch belongs to"
                                                className="bg-background-light border-2 border-border rounded-2xl px-4 py-3 outline-none text-text"
                                            />
                                        </Field>
                                        <Field label="Campaign">
                                            <input
                                                type="text"
                                                value={bulkForm.campaign}
                                                onChange={(event) => setBulkForm((current) => ({ ...current, campaign: event.target.value }))}
                                                placeholder="Campaign label"
                                                className="bg-background-light border-2 border-border rounded-2xl px-4 py-3 outline-none text-text"
                                            />
                                        </Field>
                                        <Field label="Expiration Date" optional>
                                            <input
                                                type="date"
                                                value={bulkForm.expiresAt}
                                                onChange={(event) => setBulkForm((current) => ({ ...current, expiresAt: event.target.value }))}
                                                className="bg-background-light border-2 border-border rounded-2xl px-4 py-3 outline-none text-text"
                                            />
                                        </Field>
                                        <Field label="Notes" optional>
                                            <textarea
                                                value={bulkForm.notes}
                                                onChange={(event) => setBulkForm((current) => ({ ...current, notes: event.target.value }))}
                                                placeholder="Shared notes for the whole batch"
                                                rows={3}
                                                className="bg-background-light border-2 border-border rounded-2xl px-4 py-3 outline-none text-text resize-none"
                                            />
                                        </Field>
                                    </div>

                                    <button
                                        onClick={() => handleGenerate('bulk')}
                                        disabled={submittingBulk}
                                        className="mt-5 w-full rounded-2xl border-2 border-primary bg-primary px-5 py-3 text-background font-bold disabled:opacity-60"
                                    >
                                        {submittingBulk ? 'Generating batch...' : 'Generate bulk codes'}
                                    </button>
                                </section>
                            </div>

                            <section className="rounded-[1.75rem] border-2 border-border bg-background-pop p-6 shadow-xl shadow-black/10">
                                <div className="flex items-start justify-between gap-4">
                                    <div>
                                        <p className="text-xs uppercase tracking-[0.24em] text-text-light">Saved History</p>
                                        <h2 className="mt-3 text-2xl font-extrabold text-white">Generated entries</h2>
                                    </div>
                                    <button
                                        onClick={loadData}
                                        className="rounded-2xl border-2 border-border px-4 py-2 font-bold text-text hover:bg-background-light transition-colors"
                                    >
                                        Refresh
                                    </button>
                                </div>

                                {message && (
                                    <div className="mt-5 rounded-2xl border border-border bg-background-light px-4 py-3 text-sm text-text">
                                        {message}
                                    </div>
                                )}

                                {couponMessage && (
                                    <div className="mt-3 rounded-2xl border border-primary/40 bg-primary/10 px-4 py-3 text-sm text-text">
                                        {couponMessage}
                                    </div>
                                )}

                                <div className="mt-6 grid gap-4">
                                    {pageLoading ? (
                                        <div className="rounded-2xl border border-border bg-background-light px-4 py-10 text-center text-text-light">
                                            Loading saved entries...
                                        </div>
                                    ) : entries.length === 0 ? (
                                        <div className="rounded-2xl border border-border bg-background-light px-4 py-10 text-center text-text-light">
                                            No codes generated yet.
                                        </div>
                                    ) : (
                                        entries.map((entry) => (
                                            <BatchCard
                                                key={entry.key}
                                                entry={entry}
                                                onCopy={handleCopy}
                                                onDelete={handleDelete}
                                                deleting={deletingBatchKey === entry.key}
                                            />
                                        ))
                                    )}
                                </div>
                            </section>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default AdminCharityCodes;
